// @flow
import { Fragment, useLayoutEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { StickyContainer, Sticky } from 'react-sticky';
import { stylesheet } from 'typestyle';
import * as csx from 'csstips';

import Jumpbar from '@components/diamondSearch/Jumpbar';
import Headers from '@components/diamondSearch/Headers';
import DiamondDatagrid from '@components/datagrids/DiamondDatagrid';

import Loading from '@components/icons/Loading';

import { linkBlueText } from '@styles/general';

import { columnWidths as headerColumnWidths } from '@components/datagrids/headers/DiamondDatagridHeader';
import { columnWidths as rowColumnWidths } from '@components/datagrids/rows/DiamondDatagridRow';

import {
  calculateColumnBreakpoints,
  calculateBreakpointClassesAndMinTableWidth
} from '@src/utils/diamondSearchResponsiveColumnCalculator';

type TProps = {
  dyor: boolean
};

const css = stylesheet({
  loadingOverlay: {
    minHeight: '300px',
    textAlign: 'center',
    lineHeight: '300px'
  },
  noMatches: {
    minHeight: '300px',
    textAlign: 'center',
    lineHeight: '300px',
    fontSize: '25px',
    color: '#a2a2a2'
  },
  scrollDownContainer: {
    ...csx.horizontal,
    ...csx.centerCenter,
    padding: '10px 0'
  },
  scrollDown: {
    fontWeight: 'bold',
    color: linkBlueText,
    margin: 0
  }
});

const calculateResponsiveColumns = (selectedFilters, allowCompare) => {
  const [
    headerResponsiveBreakpoints,
    rowResponsiveBreakpoints
  ] = calculateColumnBreakpoints(
    selectedFilters,
    headerColumnWidths,
    rowColumnWidths,
    allowCompare
  );
  const headerResponsiveColumns = calculateBreakpointClassesAndMinTableWidth(
    headerResponsiveBreakpoints,
    allowCompare
  );
  const rowResponsiveColumns = calculateBreakpointClassesAndMinTableWidth(
    rowResponsiveBreakpoints,
    allowCompare
  );

  return [headerResponsiveColumns, rowResponsiveColumns];
};

function Results (props: TProps) {
  const stickyContainerRef = useRef(null);
  const filters = useSelector((state) => state.diamondSearchFilters);
  const loadedCount = useSelector((state) => state.diamonds.matches.length);
  const matchCount = useSelector((state) => state.diamonds.count);
  const isLoading = useSelector((state) => state.diamondSearch.loading);
  const [headerResponsiveColumns, setHeaderResponsiveColumns] = useState({});
  const [rowResponsiveColumns, setRowResponsiveColumns] = useState({});
  const [columnWidthsCalculated, setColumnWidthsCalculated] = useState(false);

  const allDiamondsAreLoaded = loadedCount === matchCount;
  const { dyor } = props;

  useLayoutEffect(
    () => {
      const selectedFilters = Object.keys(filters).filter(
        (f) => (filters[f] || []).length > 0
      );

      const [
        headerResponsiveColumns,
        rowResponsiveColumns
      ] = calculateResponsiveColumns(selectedFilters, true);

      setHeaderResponsiveColumns(headerResponsiveColumns);
      setRowResponsiveColumns(rowResponsiveColumns);

      if (!columnWidthsCalculated) {
        setColumnWidthsCalculated(true);
      }
    },
    [dyor, columnWidthsCalculated, filters]
  );

  return (
    <StickyContainer
      style={{
        overflowX: 'scroll',
        overflowY: 'hidden',
        WebkitOverflowScrolling: 'touch'
      }}
      ref={(node) => {
        if (node) stickyContainerRef.current = node.node;
      }}>
      {/* This div is simply a placeholder because the jumpbar otherwise
                 gets positioned at the top of the page for a split second on
                 load */}
      <div style={{ position: 'relative' }}>
        <Sticky>
          {({ style, isSticky }) => (
            <div
              style={{
                ...style,
                height: 100,
                paddingTop: 50,
                background: 'white',
                zIndex: 1
              }}>
              {columnWidthsCalculated ? (
                <Jumpbar
                  tableMinimumWidth={
                    headerResponsiveColumns['tableMinimumWidth']
                  }
                  stickyContainerRef={stickyContainerRef}
                />
              ) : null}
              {columnWidthsCalculated ? (
                <Headers
                  headerResponsiveColumns={headerResponsiveColumns}
                  dyor={dyor}
                />
              ) : null}
            </div>
          )}
        </Sticky>
      </div>
      {columnWidthsCalculated ? (
        <Fragment>
          <DiamondDatagrid
            rowResponsiveColumns={rowResponsiveColumns}
            dyor={dyor}
          />

          {isLoading && (
            <div className={css.loadingOverlay}>
              <Loading />
            </div>
          )}
          {matchCount === 0 &&
          !isLoading && <div className={css.noMatches}>No matches found</div>}
          {!isLoading && !allDiamondsAreLoaded ? (
            <div className={css.scrollDownContainer}>
              <p className={css.scrollDown}>
                <i className="fa fa-arrow-down" /> Scroll down to see more
              </p>
            </div>
          ) : null}
        </Fragment>
      ) : null}
    </StickyContainer>
  );
}

export default Results;
